import React from 'react';
import { Box, Typography, Paper, Button, Chip, Skeleton, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

/**
 * NextGenPortalUI — shared presentation-layer design system for the
 * TSG Nexus (NextGen Builder) student portal.
 *
 * Presentation-only. No API calls, no business logic, no state management.
 * Import these primitives into page components to keep the portal visually
 * consistent (cards, typography, spacing, empty states, status chips).
 */

/* ─────────────────────────── Design Tokens ─────────────────────────── */

export const TOKENS = {
  brand: {
    primary: '#6C3BFF',
    primaryLight: '#9B6FFF',
    primaryDark: '#5020E0',
    secondary: '#8B5CF6',
    gradient: 'linear-gradient(135deg, #6C3BFF 0%, #8B5CF6 100%)',
    gradientHover: 'linear-gradient(135deg, #5B2FE6 0%, #7C3AED 100%)',
  },
  accent: {
    cyan: '#06B6D4',
    green: '#16A34A',
    orange: '#F59E0B',
  },
  bg: {
    page: '#F8FAFC',
    subtle: '#FAFBFC',
    card: '#FFFFFF',
  },
  border: '#E5E7EB',
  borderSoft: '#EEF0F3',
  text: {
    primary: '#111827',
    secondary: '#374151',
    muted: '#6B7280',
    caption: '#4B5563',
    onBrand: '#FFFFFF',
  },
  radius: { card: '16px', control: '10px', pill: 999 },
  shadow: {
    card: '0 1px 2px rgba(15,23,42,0.04), 0 1px 3px rgba(15,23,42,0.03)',
    cardHover: '0 12px 32px rgba(15,23,42,0.08)',
    brand: '0 8px 20px rgba(108,59,255,0.22)',
  },
};

/* ─────────────────────────── Layout ─────────────────────────── */

/** Centered page container — 1400px max width, responsive padding. */
export const PageContainer = ({ children, sx = {}, maxWidth = 1400 }) => (
  <Box
    sx={{
      maxWidth,
      mx: 'auto',
      px: { xs: 2, sm: 3, md: 4 },
      py: { xs: 2.5, sm: 3, md: 3.5 },
      ...sx,
    }}
  >
    {children}
  </Box>
);

/** Page-level heading row: eyebrow/icon + title + description + optional action. */
export const PageHeader = ({ icon, eyebrow, title, description, action, sx = {} }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: { xs: 'flex-start', sm: 'center' },
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 2,
      mb: { xs: 3, md: 4 },
      ...sx,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
      {icon && (
        <Box
          sx={{
            width: 48, height: 48, borderRadius: '14px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: TOKENS.brand.gradient,
            color: '#FFFFFF',
            boxShadow: TOKENS.shadow.brand,
            flexShrink: 0,
            '& svg': { fontSize: 24 },
          }}
        >
          {icon}
        </Box>
      )}
      <Box>
        {eyebrow && (
          <Typography
            variant="overline"
            sx={{ color: TOKENS.brand.primary, fontWeight: 700, letterSpacing: '0.08em', display: 'block', lineHeight: 1.4 }}
          >
            {eyebrow}
          </Typography>
        )}
        <Typography
          variant="h5"
          sx={{ fontWeight: 800, color: TOKENS.text.primary, letterSpacing: '-0.02em', fontSize: { xs: '1.3rem', md: '1.55rem' } }}
        >
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" sx={{ color: TOKENS.text.muted, fontWeight: 500, mt: 0.4, maxWidth: 560 }}>
            {description}
          </Typography>
        )}
      </Box>
    </Box>
    {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
  </Box>
);

/* ─────────────────────────── Cards ─────────────────────────── */

/** Standard white card container: rounded, soft shadow, thin border, hover elevation. */
export const SectionCard = ({
  children, sx = {}, hoverElevate = false, padding = { xs: 2.5, md: 3.5 }, component, ...rest
}) => (
  <Paper
    elevation={0}
    component={component || (hoverElevate ? motion.div : 'div')}
    {...(hoverElevate ? { whileHover: { y: -3, boxShadow: TOKENS.shadow.cardHover }, transition: { duration: 0.2 } } : {})}
    sx={{
      background: TOKENS.bg.card,
      border: `1px solid ${TOKENS.border}`,
      borderRadius: TOKENS.radius.card,
      boxShadow: TOKENS.shadow.card,
      p: padding,
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Paper>
);

/** Section title row used inside a SectionCard: icon + title + optional action link. */
export const SectionTitle = ({ icon, title, action, sx = {} }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.25, ...sx }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
      {icon && (
        <Box sx={{ color: TOKENS.brand.primaryLight, display: 'flex', '& svg': { fontSize: 20 } }}>
          {icon}
        </Box>
      )}
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: TOKENS.text.primary, fontSize: '1rem' }}>
        {title}
      </Typography>
    </Box>
    {action}
  </Box>
);

/** Taller premium stat card with icon chip, gradient accent bar, and hover lift. */
export const StatCard = ({ icon, label, value, subtitle, color = TOKENS.brand.primary, loading, trend }) => (
  <Paper
    elevation={0}
    component={motion.div}
    whileHover={{ y: -4, boxShadow: `0 16px 36px ${color}26` }}
    transition={{ duration: 0.2 }}
    sx={{
      p: { xs: 2.25, md: 2.75 },
      borderRadius: TOKENS.radius.card,
      background: TOKENS.bg.card,
      border: `1px solid ${TOKENS.border}`,
      boxShadow: TOKENS.shadow.card,
      position: 'relative',
      overflow: 'hidden',
      minHeight: 148,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      '&::before': {
        content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${color} 0%, ${color}66 100%)`,
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
      <Box
        sx={{
          width: 44, height: 44, borderRadius: '12px',
          background: `${color}14`, border: `1px solid ${color}28`,
          color, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {icon && React.cloneElement(icon, { sx: { fontSize: 22 } })}
      </Box>
      {trend && (
        <Chip
          size="small"
          label={trend}
          sx={{
            height: 22, fontSize: '0.68rem', fontWeight: 700,
            bgcolor: 'rgba(22,163,74,0.1)', color: '#16A34A', border: '1px solid rgba(22,163,74,0.22)',
          }}
        />
      )}
    </Box>
    {loading ? (
      <Stack spacing={0.75}>
        <Skeleton variant="text" width="60%" height={40} />
        <Skeleton variant="text" width="80%" height={20} />
      </Stack>
    ) : (
      <Box>
        <Typography sx={{ fontWeight: 800, color: TOKENS.text.primary, fontSize: { xs: '1.5rem', md: '1.75rem' }, lineHeight: 1.1, letterSpacing: '-0.02em', mb: 0.5 }}>
          {value}
        </Typography>
        <Typography variant="body2" sx={{ color: TOKENS.text.secondary, fontWeight: 600, fontSize: '0.83rem' }}>
          {label}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: TOKENS.text.muted, display: 'block', mt: 0.4, fontWeight: 500 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    )}
  </Paper>
);

/* ─────────────────────────── Empty States ─────────────────────────── */

/** Illustration + headline + description + optional action button. */
export const EmptyState = ({ icon, title, description, action, minHeight = 240 }) => (
  <Box
    sx={{
      py: 5, px: 3, textAlign: 'center', minHeight,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    }}
  >
    <Box
      sx={{
        width: 72, height: 72, borderRadius: '20px', mb: 2.25,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, rgba(108,59,255,0.1), rgba(139,92,246,0.06))',
        border: '1px solid rgba(108,59,255,0.16)',
        color: TOKENS.brand.primary,
      }}
    >
      {icon && React.cloneElement(icon, { sx: { fontSize: 32 } })}
    </Box>
    <Typography sx={{ fontWeight: 700, color: TOKENS.text.primary, fontSize: '1.02rem', mb: 0.75 }}>
      {title}
    </Typography>
    {description && (
      <Typography variant="body2" sx={{ color: TOKENS.text.muted, fontWeight: 500, maxWidth: 340, mb: action ? 2.5 : 0 }}>
        {description}
      </Typography>
    )}
    {action}
  </Box>
);

/* ─────────────────────────── Buttons ─────────────────────────── */

export const GradientButton = ({ children, sx = {}, endIcon, ...rest }) => (
  <Button
    variant="contained"
    endIcon={endIcon !== null ? (endIcon || <ArrowForwardRoundedIcon sx={{ fontSize: '16px !important' }} />) : undefined}
    sx={{
      background: TOKENS.brand.gradient,
      color: '#FFFFFF',
      borderRadius: TOKENS.radius.control,
      fontWeight: 700,
      px: 2.5,
      py: 1,
      boxShadow: TOKENS.shadow.brand,
      '&:hover': { background: TOKENS.brand.gradientHover, boxShadow: '0 10px 24px rgba(108,59,255,0.3)' },
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Button>
);

/* ─────────────────────────── Status Chips ─────────────────────────── */

const STATUS_STYLES = {
  success: { bg: 'rgba(22,163,74,0.1)', fg: '#16A34A', border: 'rgba(22,163,74,0.24)' },
  active: { bg: 'rgba(22,163,74,0.1)', fg: '#16A34A', border: 'rgba(22,163,74,0.24)' },
  completed: { bg: 'rgba(22,163,74,0.1)', fg: '#16A34A', border: 'rgba(22,163,74,0.24)' },
  pending: { bg: 'rgba(245,158,11,0.12)', fg: '#B45309', border: 'rgba(245,158,11,0.26)' },
  warning: { bg: 'rgba(245,158,11,0.12)', fg: '#B45309', border: 'rgba(245,158,11,0.26)' },
  processing: { bg: 'rgba(6,182,212,0.12)', fg: '#0E7490', border: 'rgba(6,182,212,0.26)' },
  failed: { bg: 'rgba(239,68,68,0.1)', fg: '#DC2626', border: 'rgba(239,68,68,0.24)' },
  error: { bg: 'rgba(239,68,68,0.1)', fg: '#DC2626', border: 'rgba(239,68,68,0.24)' },
  default: { bg: 'rgba(108,59,255,0.1)', fg: '#6C3BFF', border: 'rgba(108,59,255,0.22)' },
};

/** Color-coded status chip. Pass `tone` explicitly, or it's inferred from `label`. */
export const StatusChip = ({ label, tone, icon, size = 'small', sx = {} }) => {
  const key = (tone || label || '').toString().toLowerCase();
  const style = STATUS_STYLES[key] || STATUS_STYLES.default;
  return (
    <Chip
      size={size}
      icon={icon}
      label={label}
      sx={{
        bgcolor: style.bg,
        color: style.fg,
        border: `1px solid ${style.border}`,
        fontWeight: 700,
        fontSize: '0.72rem',
        height: size === 'small' ? 24 : 28,
        '& .MuiChip-icon': { color: style.fg, fontSize: '14px !important', ml: '6px' },
        ...sx,
      }}
    />
  );
};

/* ─────────────────────────── Motion presets ─────────────────────────── */

export const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] },
};

export const staggerContainer = {
  animate: { transition: { staggerChildren: 0.06 } },
};

export default {
  TOKENS,
  PageContainer,
  PageHeader,
  SectionCard,
  SectionTitle,
  StatCard,
  EmptyState,
  GradientButton,
  StatusChip,
  fadeUp,
  staggerContainer,
};
